import {useEffect} from 'react'
import {useRouter} from 'next/router'
import {logMessage} from '@/services/logger/client'

declare global {
	interface Window {
		pearCommerceLoaded?: {
			productLocator?: boolean
			retailerList?: boolean
		}
	}
}

interface PearCommerceCoordinatorProps {
	pearCommerceId: string | null
}

/**
 * Checks if an existing Pear SDK script already covers the needed widgets.
 *
 * Handles both legacy widget field and new widgets array for backward compatibility.
 * On parse failure, returns false to allow the page to recover with a fresh SDK load.
 *
 * @param widgets - Array of widget names needed for the current page
 * @returns true if existing script covers all needed widgets, false otherwise
 */
function existingScriptCoversWidgets(widgets: string[]): boolean {
	const existingScript = document.querySelector(
		'script[data-pear-commerce-sdk]'
	)
	if (!existingScript || !(existingScript instanceof HTMLScriptElement)) {
		return false
	}

	try {
		// Parse the existing script's config to see what widgets it has
		const url = new URL(existingScript.src)
		const configParam = url.searchParams.get('config')
		if (!configParam) {
			// If we can't find config, assume it doesn't cover our needs
			return false
		}

		const existingConfig = JSON.parse(configParam) as {
			widget?: string | string[]
			widgets?: string | string[]
		}

		// Normalize both legacy widget and new widgets fields to array format
		let existingWidgets: string[] = []
		if (Array.isArray(existingConfig.widgets)) {
			existingWidgets = existingConfig.widgets
		} else if (typeof existingConfig.widgets === 'string') {
			existingWidgets = [existingConfig.widgets]
		} else if (Array.isArray(existingConfig.widget)) {
			existingWidgets = existingConfig.widget
		} else if (typeof existingConfig.widget === 'string') {
			// Handle legacy widget field as string
			existingWidgets = [existingConfig.widget]
		}

		// Check if existing script covers all needed widgets
		return widgets.every((w) => existingWidgets.includes(w))
	} catch {
		// If we can't parse the config, assume it doesn't cover our needs
		// so the page can recover by issuing a fresh SDK request
		return false
	}
}

/**
 * Coordinator component that loads Pear Commerce SDK only when needed.
 * Detects the presence of Pear Commerce blocks on the page and loads
 * the appropriate widget configurations.
 */
export function PearCommerceCoordinator({
	pearCommerceId,
}: PearCommerceCoordinatorProps): null {
	const router = useRouter()

	useEffect(() => {
		if (!pearCommerceId) return

		// Initialize tracking object (use a local reference to avoid non-null assertions)
		const win = globalThis as typeof globalThis & Window
		const pearLoaded =
			win.pearCommerceLoaded ?? (win.pearCommerceLoaded = {})

		// Detect all required widgets upfront
		const hasProductLocator = Boolean(
			document.querySelector('#pear-map-target')
		)
		const hasRetailerList = Boolean(
			document.querySelector('#pear-pdp-target')
		)

		// If no widgets detected, nothing to do
		if (!hasProductLocator && !hasRetailerList) {
			return
		}

		// If all detected widgets are already loaded, nothing to do
		if (
			(!hasProductLocator || pearLoaded.productLocator) &&
			(!hasRetailerList || pearLoaded.retailerList)
		) {
			return
		}

		// Determine which widgets to load
		const widgets: string[] = []
		if (hasProductLocator && !pearLoaded.productLocator) {
			widgets.push('product-locator')
		}
		if (hasRetailerList && !pearLoaded.retailerList) {
			widgets.push('retailer-list')
		}

		// If all needed widgets are already loaded, nothing to do
		if (widgets.length === 0) return

		// Guard against in-flight requests for the same widget set
		// Only block if existing script already covers all widgets we need
		if (existingScriptCoversWidgets(widgets)) {
			return
		}

		// Load a single SDK instance with all required widgets
		const script = document.createElement('script')
		script.type = 'text/javascript'
		script.dataset.pearCommerceSdk = 'true'
		script.src = `https://offers.pearcommerce.com/js/sdk.js?config=${encodeURIComponent(
			JSON.stringify({
				vendorId: pearCommerceId,
				widget: widgets.length === 1 ? widgets[0] : widgets,
			})
		)}`

		script.onload = () => {
			logMessage(
				`Pear Commerce SDK loaded with widgets: ${widgets.join(', ')}`
			)
			// Mark all loaded widgets as complete
			if (hasProductLocator) {
				pearLoaded.productLocator = true
			}
			if (hasRetailerList) {
				pearLoaded.retailerList = true
			}
		}
		script.onerror = () => {
			logMessage(
				`Failed to load Pear Commerce SDK with widgets: ${widgets.join(', ')}`
			)
			// Remove failed script so retry can occur on next navigation
			script.remove()
			// Reset flags so retry can occur on next navigation
			if (hasProductLocator) {
				pearLoaded.productLocator = false
			}
			if (hasRetailerList) {
				pearLoaded.retailerList = false
			}
		}

		document.head.appendChild(script)
		// Set flags immediately to prevent race condition
		if (hasProductLocator) {
			pearLoaded.productLocator = true
		}
		if (hasRetailerList) {
			pearLoaded.retailerList = true
		}
	}, [pearCommerceId, router.asPath])

	return null
}
